import { isFlightItem } from 'checkout/lib/utils/flights/cart'
import { getCheckoutFlightV2SearchUrl, getStandaloneFlightsV2SearchUrl } from 'checkout/lib/utils/flights/links'
import { aggregateFlightServiceRows, type FlightServiceRowData } from 'checkout/Components/Shared/Summary/BreakdownItems/flightServiceRows'
import { arrayToMap, arrayToObject, nonNullable, sum } from 'lib/array/arrayUtils'
import {
  buildDateChangeFlightItemBreakdownView,
  buildFlightItemBreakdownView,
  formatFareType,
  formatFlightPassengersShorthand, formatJourneyShorthand, formatJourneySummaryTitle,
  formatTravellerCount,
} from 'lib/checkout/flights/format'
import config from 'constants/config'
import { createSelector } from 'reselect'

import { getDateChangeJourneyV2IdKey, getFlightLoyaltyProductType, getFlightLoyaltyProductTypeRank, getJourneyV2IdKey, hasCheckoutFlightItemsWithEmbeddedInsurance } from 'lib/flights/flightUtils'
import { getFlightItemViewPrice } from 'checkout/lib/utils/flights/getFlightItemViewPrice'
import { getFlightDateChangeSearchKey } from 'checkout/lib/utils/flights/dateChangeUtils'
import { FlightChangeDatesPageStep } from 'components/App/Header/HeaderCheckout/StepsHeaderCheckout/steps/flights'
import { convertJourneyV2ToJourneyFlight } from 'api/lib/convertJourneyV2'
import { FlightsFareTypes } from 'constants/flight'
import { findPostPurchaseCheckout, isBookingProtectionItem, isInsuranceItem, isLuxPlusSubscriptionItem } from 'lib/checkout/checkoutUtils'
import { reselectShallowEqual } from 'lib/selector/selectorUtils'
import { getAllOrderItems, isBookingProtectionOrderItem, isFlightOrderItem, isInsuranceOrderItem, isLuxPlusSubscriptionOrderItem } from 'lib/order/orderUtils'
import { getFlightSeatSelectionsByKey } from 'checkout/selectors/view/flightSeatSelections'

export const getFlightItems = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): Array<App.Checkout.FlightItem> => items.filter(isFlightItem),
  reselectShallowEqual,
)

export const flightSearchInProgress = createSelector(
  (state: App.State) => state.flights.searchFlights,
  (state: App.State) => state.flights.searchV2Flights,
  (flightsV1, flightsV2): boolean => {
    const journeysV1Fetching = Object.values(flightsV1).flatMap(flight => flight?.fetching).filter(Boolean)
    const journeysV2Fetching = Object.values(flightsV2).flatMap(flight => flight?.fetching).filter(Boolean)
    return !!(journeysV1Fetching.length || journeysV2Fetching.length)
  },
)

export const getBundledFlightItems = createSelector(
  (state: App.State) => getFlightItems(state),
  (items): Array<App.Checkout.FlightItem> => items.filter(item => !!item.bundledItemIds?.length),
  reselectShallowEqual,
)

export const cartIncludesFlights = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): boolean => items.some(isFlightItem),
)

const createTravellerElements = (travellers: Array<{ type: string }> = [], penaltyBreakdown: Record<string, App.FlightChangePenaltyBreakdown> = {}) => {
  const travellerCounts = {
    adult: travellers.filter(t => t.type === 'adult').length,
    child: travellers.filter(t => t.type === 'child').length,
    infant: travellers.filter(t => t.type === 'infant').length,
  }

  const priceMap = {
    adult: penaltyBreakdown.adult?.amount || 0,
    child: penaltyBreakdown.child?.amount || 0,
    infant: penaltyBreakdown.infant?.amount || 0,
  }

  return Object.entries(travellerCounts)
    .filter(([_, count]) => count > 0)
    .map(([type, count]) => ({
      id: type,
      left: {
        type: 'text' as const,
        value: formatTravellerCount(type, count),
      },
      right: {
        type: 'price' as const,
        value: priceMap[type as keyof typeof priceMap] * count,
      },
    }))
}

function getSelectedDateChangeJourneys(
  flightItems: Array<App.Checkout.FlightItem>,
  journeysById: Record<string, App.AnyJourney>,
): { hasSelectedJourneys: boolean; hasRequiredData: boolean; journeys: Array<App.JourneyV2> } {
  const journeyKeys = flightItems
    .filter(item => item.isFlightDateChange)
    .flatMap(item => item.flights.map(flight => getDateChangeJourneyV2IdKey(item.reservationId!, item.searchId!, flight.journeyId)))
  const journeys = journeyKeys.map(key => journeysById[key] as App.JourneyV2 | undefined)

  return {
    hasSelectedJourneys: journeyKeys.length > 0,
    hasRequiredData: journeyKeys.length > 0 && journeys.every(Boolean),
    journeys: journeys.filter((journey): journey is App.JourneyV2 => !!journey),
  }
}

function getFirstAvailableJourneyPerLeg(fares: Array<Array<App.JourneyV2>> = []): Array<App.JourneyV2> {
  return fares
    .map(legFares => legFares[0])
    .filter((journey): journey is App.JourneyV2 => !!journey)
}

const passengerPriceKeys = ['adult', 'child', 'infant'] as const

function createAirlineChangeFeeTravellerElements(journeys: Array<App.JourneyV2>) {
  return passengerPriceKeys
    .map(type => {
      const firstPrice = journeys.find(journey => journey.price?.[type])?.price?.[type]
      const count = firstPrice?.passengerTypeQuantity.quantity ?? 0
      const price = sum(journeys, journey => journey.price?.[type]?.airlineChangeFee ?? 0)

      return { type, count, price }
    })
    .filter(({ count }) => count > 0)
    .map(({ type, count, price }) => ({
      id: type,
      left: {
        type: 'text' as const,
        value: formatTravellerCount(type, count),
      },
      right: {
        type: 'price' as const,
        value: price * count,
      },
    }))
}

function isBeforeFlightDateChangeNewFlightSelection(currentStep?: string): boolean {
  return currentStep === FlightChangeDatesPageStep.SELECT_FLIGHTS_TO_CHANGE ||
    currentStep === FlightChangeDatesPageStep.SELECT_DATES_TO_CHANGE
}

export const getIsBeforeFlightDateChangeNewFlightSelection = createSelector(
  (state: App.State) => state.flights.flightsDateChange?.currentStep,
  isBeforeFlightDateChangeNewFlightSelection,
)

export const getShouldHideEstimatedAirlineChangeFee = getIsBeforeFlightDateChangeNewFlightSelection

const getCurrentFlightDateChangeSearchState = createSelector(
  (state: App.State) => state.checkout.cart.existingOrder?.flightItems[0]?.reservationId,
  (state: App.State) => state.flights.flightsDateChange?.selectedLegWithNewDepartureDate,
  (state: App.State) => state.flights.flightsDateChangeSearch,
  (reservationId, selectedLegsWithDates, flightsDateChangeSearch) => {
    if (!reservationId || !selectedLegsWithDates?.length) {
      return undefined
    }

    const searchKey = getFlightDateChangeSearchKey(reservationId, selectedLegsWithDates)
    return flightsDateChangeSearch?.[searchKey]
  },
)

export const getFlightDateChangeSearchAirlineFeeJourneys = createSelector(
  (state: App.State) => getCurrentFlightDateChangeSearchState(state),
  currentSearchState => currentSearchState?.isFetching ? [] : getFirstAvailableJourneyPerLeg(currentSearchState?.fares),
)

export const getIsFlightDateChangeSearchLoading = createSelector(
  (state: App.State) => state.flights.flightsDateChange?.currentStep,
  (state: App.State) => state.checkout.cart.existingOrder?.flightItems[0]?.reservationId,
  (state: App.State) => state.flights.flightsDateChange?.selectedLegWithNewDepartureDate,
  (state: App.State) => getCurrentFlightDateChangeSearchState(state),
  (currentStep, reservationId, selectedLegsWithDates, currentSearchState): boolean => {
    if (
      currentStep !== FlightChangeDatesPageStep.SELECT_NEW_FLIGHTS ||
      !reservationId ||
      !selectedLegsWithDates?.length
    ) {
      return false
    }

    return !currentSearchState || currentSearchState.isFetching
  },
)

export const getIsCurrentFlightDateChangeSelectionValid = createSelector(
  (state: App.State) => state.flights.flightsDateChange?.currentStep,
  (state: App.State) => getFlightItems(state),
  (state: App.State) => getCurrentFlightDateChangeSearchState(state),
  (currentStep, flightItems, currentSearchState): boolean => {
    const dateChangeFlightItems = flightItems.filter(item => item.isFlightDateChange)

    if (dateChangeFlightItems.length === 0 || isBeforeFlightDateChangeNewFlightSelection(currentStep)) {
      return false
    }

    if (currentStep === FlightChangeDatesPageStep.SELECT_NEW_FLIGHTS) {
      return !!currentSearchState?.searchId &&
        !currentSearchState.isFetching &&
        dateChangeFlightItems.every(item => item.searchId === currentSearchState.searchId)
    }

    return true
  },
)

export const getIsFlightDateChangeAirlineChangeFeeLoading = createSelector(
  (state: App.State) => getIsFlightDateChangeSearchLoading(state),
  (state: App.State) => getIsCurrentFlightDateChangeSelectionValid(state),
  (state: App.State) => getFlightItems(state),
  (state: App.State) => state.flights.journeysById,
  (isFlightDateChangeSearchLoading, isCurrentFlightDateChangeSelectionValid, flightItems, journeysById): boolean => {
    if (isFlightDateChangeSearchLoading) {
      return true
    }

    const selectedDateChangeJourneyState = isCurrentFlightDateChangeSelectionValid ?
        getSelectedDateChangeJourneys(flightItems, journeysById) :
        { hasSelectedJourneys: false, hasRequiredData: false, journeys: [] }

    return selectedDateChangeJourneyState.hasSelectedJourneys && !selectedDateChangeJourneyState.hasRequiredData
  },
)

function mapFlightV2ToView(
  item: App.Checkout.FlightItem,
  airports: Array<App.Airport>,
  journeys: Record<string, App.JourneyV2> = {},
): App.Checkout.FlightItemView {
  const surcharge = sum(item.flights.map(flight => journeys[flight.journeyId]?.price.all.totalFees ?? 0))

  const isBundled = (item.bundledItemIds?.length ?? 0) > 0

  const departing = item.flights[0] ? journeys[item.flights[0].journeyId] : undefined
  const returning = item.flights[1] ? journeys[item.flights[1].journeyId] : undefined

  const isNDC = Object.values(journeys).some(journey => journey?.bookingInfo?.offerItemId && journey?.bookingInfo?.offerId)

  const price = getFlightItemViewPrice(item, journeys)

  const flightsWithJourneys = item.flights.filter(flight => journeys[flight.journeyId])

  const airportsByKey = arrayToMap(airports, airport => airport.code)

  const usedAirports: Array<{ departing?: App.Airport, arrival?: App.Airport }> = flightsWithJourneys.map(journey => {
    const departing = airportsByKey.get(journey.departingAirportCode)
    const arrival = airportsByKey.get(journey.arrivalAirportCode)

    return {
      departing,
      arrival,
    }
  })

  const firstDepartingCountryCode = usedAirports[0]?.departing?.countryCode

  const isDomestic = usedAirports.every(airport => {
    return airport.arrival?.countryCode === airport.departing?.countryCode && airport.departing?.countryCode === firstDepartingCountryCode
  })

  const luxProductTypes = flightsWithJourneys.map(journey => {
    return getFlightLoyaltyProductType(journey.fareFamily?.cabin?.toLowerCase() || item.selectedCabinClass || '', isDomestic)
  })

  const rankedProductTypes = luxProductTypes.sort((a, b) => {
    return getFlightLoyaltyProductTypeRank(a) - getFlightLoyaltyProductTypeRank(b)
  })

  const flights = flightsWithJourneys.map(flight => ({
    journeyFlight: convertJourneyV2ToJourneyFlight(journeys[flight.journeyId]),
    extras: flight.extras,
    cost: flight.cost,
    fareFamily: flight.fareFamily,
    withEmbeddedInsurance: flight.withEmbeddedInsurance,
  }))

  const result: App.Checkout.FlightItemView = {
    title: `${flights.map(flight => formatJourneyShorthand(flight.journeyFlight)).join(' - ')} - ${formatFareType(item.fareType)}`,
    loyalty: {
      productType: rankedProductTypes[0] || 'dom_economy',
    },
    item,
    journeyId: item.searchId,
    provider: departing?.provider,
    passengers: item.passengers,
    flights,
    designation: 'Flights',
    validatingCarrier: departing?.carrier,
    validatingCarrierName: departing?.carrierName ?? '',
    validatingCarrierLogo: departing?.carrierLogo ?? '',
    returningCarrierName: returning?.carrierName ?? '',
    returningCarrierLogo: returning?.carrierLogo ?? '',
    fareType: item.fareType,
    totals: {
      price,
      memberPrice: 0, // No data on this
      value: price,
      surcharge,
      memberValue: 0,
      extraGuestSurcharge: 0,
      taxesAndFees: 0,
      otherFees: item.otherFees,
    },
    seatSelectionPolicy: departing?.flightGroup.flights[0].seatSummary.selectionPolicy,
    isBundled,
    quotedFare: item.quotedFare,
    searchFlightsUrl: isBundled ?
        getCheckoutFlightV2SearchUrl(flightsWithJourneys.map(flight => journeys[flight.journeyId])) :
        getStandaloneFlightsV2SearchUrl(item, airports, journeys),
    viewType: item.viewType,
    isNDC,
  }

  return result
}

export const getFlightItemsView = createSelector(
  (state: App.State) => getFlightItems(state),
  (state: App.State) => state.flights.journeysById,
  (state: App.State) => state.geo.airports,
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (flightItems, journeys, airports, postPurchaseMode): App.WithDataStatus<Array<App.Checkout.FlightItemView>> => {
    const anyItemsFetching = flightItems.some(item => item.fetchingFare)

    const flightViews = nonNullable(
      flightItems.map<{ hasRequiredData: boolean, view: App.Checkout.FlightItemView | undefined }>((item) => {
        const journeyKeys = postPurchaseMode === 'flight-change-dates'
          ? item.flights.map(flight => getDateChangeJourneyV2IdKey(item.reservationId!, item.searchId!, flight?.journeyId))
          : item.flights.map(flight => getJourneyV2IdKey(flight?.journeyId, item.searchId!, flight?.fareFamily?.id))
        const flightJourneys = journeyKeys.map(key => journeys[key] as App.JourneyV2)
        const journeysByFlightJourneyId = arrayToObject(item.flights, flight => flight.journeyId, (_, index) => flightJourneys[index])

        return {
          view: mapFlightV2ToView(
            item,
            airports,
            journeysByFlightJourneyId,
          ),
          hasRequiredData: flightJourneys.filter(Boolean).length === item.flights.length,
        }
      }),
    )

    const hasAllRequiredData = flightViews.every(fv => fv.hasRequiredData) && !anyItemsFetching && flightViews.length === flightItems.length

    return {
      hasRequiredData: hasAllRequiredData,
      data: nonNullable(flightViews.map(view => view.view)),
    }
  },
)

export const getFlightBreakdownView = createSelector(
  (state: App.State) => getFlightItemsView(state),
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => getIsCurrentFlightDateChangeSelectionValid(state),
  (flightViewsWithStatus, postPurchaseMode, isCurrentFlightDateChangeSelectionValid): App.WithDataStatus<Array<App.Checkout.PriceBreakdownView>> => {
    if (postPurchaseMode === 'flight-change-dates' && !isCurrentFlightDateChangeSelectionValid) {
      return {
        hasRequiredData: true,
        data: [],
      }
    }

    const flightBreakdownViews = flightViewsWithStatus.data.map<App.Checkout.PriceBreakdownView>(view => {
      const supplementaryData = {
        validatingCarrierName: view.validatingCarrierName,
        validatingCarrierLogo: view.validatingCarrierLogo,
      }
      const breakdownView: App.Checkout.PriceBreakdownView = {
        title: postPurchaseMode === 'flight-change-dates' ? 'New flight additional cost' : formatFareType(view.fareType),
        additionalInfoText: postPurchaseMode === 'flight-change-dates'
          ? []
          : [
              ...(view.flights[0] ? [formatJourneySummaryTitle(view.flights, view.fareType as FlightsFareTypes)] : []),
              formatFlightPassengersShorthand(view.passengers),
            ],
        price: view.isBundled ? 0 : (view.totals.price ?? 0),
        memberPrice: view.isBundled ? 0 : (view.totals.memberPrice ?? 0),
        alwaysShowItemPrice: postPurchaseMode === 'flight-change-dates',
        items: postPurchaseMode === 'flight-change-dates'
          ? [buildDateChangeFlightItemBreakdownView(view)]
          : view.flights.map(flight => buildFlightItemBreakdownView(view, flight, supplementaryData)),
      }

      return breakdownView
    })

    return {
      hasRequiredData: flightViewsWithStatus.hasRequiredData,
      data: flightBreakdownViews,
    }
  },
)

export const getAirlineChangeFeeBreakdownView = createSelector(
  (state: App.State) => state.checkout.cart.existingOrder,
  (state: App.State) => state.orders.flightDetails,
  (state: App.State) => getFlightItems(state),
  (state: App.State) => state.flights.journeysById,
  (state: App.State) => getFlightDateChangeSearchAirlineFeeJourneys(state),
  (state: App.State) => getShouldHideEstimatedAirlineChangeFee(state),
  (state: App.State) => getIsFlightDateChangeAirlineChangeFeeLoading(state),
  (state: App.State) => getIsCurrentFlightDateChangeSelectionValid(state),
  (order, allFlightDetails, flightItems, journeysById, searchAirlineFeeJourneys, hideEstimatedAirlineChangeFee, isAirlineChangeFeeLoading, isCurrentFlightDateChangeSelectionValid): App.Checkout.PriceBreakdownView => {
    const selectedDateChangeJourneyState = isCurrentFlightDateChangeSelectionValid ?
        getSelectedDateChangeJourneys(flightItems, journeysById) :
        { hasSelectedJourneys: false, hasRequiredData: false, journeys: [] }
    const selectedDateChangeJourneys = selectedDateChangeJourneyState.journeys
    const airlineFeeJourneys = searchAirlineFeeJourneys.length > 0 ? searchAirlineFeeJourneys : selectedDateChangeJourneys
    const flightItemId = order?.flightItems[0]?.id
    const flightDetails = allFlightDetails[flightItemId ?? '']
    const travellers = flightDetails?.flight?.travellers || []
    const vendorFeeBreakdown = flightDetails?.flight?.flightChangePenalties?.fees.vendorFeeByCarrier?.[0]?.penaltyBreakdown || {}
    const hasAirlineFeeJourneys = airlineFeeJourneys.length > 0
    const price = hideEstimatedAirlineChangeFee || isAirlineChangeFeeLoading ? 0 : hasAirlineFeeJourneys ?
        sum(airlineFeeJourneys, journey => journey.price?.all?.airlineChangeFee ?? 0) :
      flightDetails?.flight?.flightChangePenalties?.fees.totalAmount || 0
    const items: Array<App.Checkout.AnyItemBreakdownView> = [
      {
        itemType: 'flights-date-change-fee',
        title: '',
        additionalInfo: [],
        additionalElements: hasAirlineFeeJourneys ?
            createAirlineChangeFeeTravellerElements(airlineFeeJourneys) :
            createTravellerElements(travellers, vendorFeeBreakdown),
      },
    ]

    return {
      title: 'Airline change fee',
      priceLabel: hideEstimatedAirlineChangeFee ? 'Confirmed at date selection' : undefined,
      price,
      alwaysShowItemPrice: true,
      additionalInfoText: [],
      detailInfoText: hideEstimatedAirlineChangeFee ? [
        'Change fees vary by airline and fare type. We’ll show the exact amount once you select your new flights',
      ] : undefined,
      items: hideEstimatedAirlineChangeFee || isAirlineChangeFeeLoading ? [] : items,
      memberPrice: 0,
    }
  },
)

export const getLuxuryEscapesChangeFeeBreakdownView = createSelector(
  (state: App.State) => state.checkout.cart.existingOrder,
  (state: App.State) => state.orders.flightDetails,
  (order, allFlightDetails): App.Checkout.PriceBreakdownView => {
    const flightItemId = order?.flightItems[0]?.id
    const flightDetails = allFlightDetails[flightItemId ?? '']
    const travellers = flightDetails?.flight?.travellers || []
    const luxuryEscapesChangeFee = flightDetails?.flight?.flightChangePenalties?.fees.handlingFee || 0
    const handlingFeeBreakdown = flightDetails?.flight?.flightChangePenalties?.fees.handlingFeeBreakdown || {}

    return {
      title: `${config.brandConfig.name} change fee`,
      price: luxuryEscapesChangeFee,
      alwaysShowItemPrice: true,
      additionalInfoText: [],
      items: [
        {
          itemType: 'flights-date-change-fee',
          title: '',
          additionalInfo: [],
          additionalElements: createTravellerElements(travellers, handlingFeeBreakdown),
        },
      ],
      memberPrice: 0,
    }
  },
)

export const getFlightsDateChangeBreakdownView = createSelector(
  (state: App.State) => getFlightItemsView(state),
  (state: App.State) => getAirlineChangeFeeBreakdownView(state),
  (state: App.State) => getLuxuryEscapesChangeFeeBreakdownView(state),
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => getIsFlightDateChangeAirlineChangeFeeLoading(state),
  (flightViewsWithStatus, airlineChangeFeeBreakdownView, luxuryEscapesChangeFeeBreakdownView, postPurchaseMode, isAirlineChangeFeeLoading): App.WithDataStatus<Array<App.Checkout.PriceBreakdownView>> => {
    if (postPurchaseMode !== 'flight-change-dates') {
      return {
        hasRequiredData: flightViewsWithStatus.hasRequiredData,
        data: [],
      }
    }

    return {
      hasRequiredData: flightViewsWithStatus.hasRequiredData && !isAirlineChangeFeeLoading,
      data: [
        airlineChangeFeeBreakdownView,
        // Hide the change fee section when it is $0.
        ...(luxuryEscapesChangeFeeBreakdownView.price > 0 ? [luxuryEscapesChangeFeeBreakdownView] : []),
      ],
    }
  },
)

export const isStandaloneFlights = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => getFlightItems(state),
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => state.checkout.cart.existingOrder,
  (allItems, flightItems, postPurchaseMode, order): boolean => {
    if (postPurchaseMode) {
      if (!order) return false
      return !!getAllOrderItems(order)?.every(
        item => isFlightOrderItem(item) || isInsuranceOrderItem(item) || isBookingProtectionOrderItem(item) || isLuxPlusSubscriptionOrderItem(item),
      )
    }

    // a standalone flight can still have insurance/booking protection and/or a luxplus subscription
    return flightItems.length > 0 && allItems.every(item => isFlightItem(item) || isInsuranceItem(item) || isBookingProtectionItem(item) || isLuxPlusSubscriptionItem(item))
  },
)

export const checkoutContainsFlightsWithEmbeddedInsurance = createSelector(
  (state: App.State) => getFlightItems(state),
  (flightItems) => hasCheckoutFlightItemsWithEmbeddedInsurance(flightItems),
)

/**
 * Returns aggregated flight addon rows (seat selections, baggage, meals, etc.)
 * computed directly from flight breakdown views and seat selections.
 */
export const getFlightAddonRows = createSelector(
  (state: App.State) => getFlightBreakdownView(state),
  (state: App.State) => getFlightSeatSelectionsByKey(state),
  (flightBreakdownViewWithStatus, seatSelectionsByKey): Array<FlightServiceRowData> => {
    const flightItems = flightBreakdownViewWithStatus.data
      .flatMap(breakdown => breakdown.items)
      .filter((item): item is App.Checkout.FlightItemBreakdownView => item.itemType === 'flight')

    return aggregateFlightServiceRows(flightItems, seatSelectionsByKey)
  },
)

export const getFlightNumbersBySearchId = createSelector(
  (state: App.State) => getFlightItems(state),
  (state: App.State) => state.flights.seatsMap,
  (flightItems, seatMaps) => {
    const flightNumberMap: Record<string, Array<string>> = {}

    Object.values(seatMaps).forEach((values) => {
      if (values?.data?.seatMap && !values?.fetching) {
        values.data.seatMap.forEach((seatMap) => {
          const flightItem = flightItems.find((item) => {
            return item.flights.find((flight) => flight.journeyId === seatMap.fareId)
          })

          const searchId = flightItem?.searchId ?? ''

          const mappedSearchId = flightNumberMap[searchId] ?? []
          mappedSearchId.push(seatMap.paxSegmentRefID)
          flightNumberMap[searchId] = mappedSearchId
        })
      }
    })

    return flightNumberMap
  },
)
